// ============================================================
// Mentions and completions (v8.363.1). `@name` in a line names a person
// (for the toast); the completions are what the composer's popup offers
// for the word under the caret. Pure.
// ============================================================
import type { ChatChannel, Person } from './types';

/** The uids of every `@name` in the text - the longest matching name wins at each `@`. */
export function extractMentions(text: string, people: Person[]): string[] {
  const out = new Set<string>();
  const lower = text.toLowerCase();
  const names = people.flatMap((p) => lowerNames(p).map((name) => ({ uid: p.uid, name })));
  const re = /@(\S)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(lower))) {
    const uid = longestNameAt(lower, m.index + 1, names);
    if (uid !== null) out.add(uid);
  }
  return [...out];
}

/** A person's names, lower-cased, blanks dropped: who they are, then who they act as. */
function lowerNames(p: Person): string[] {
  return [p.name, p.actingAsName ?? ''].filter(Boolean).map((n) => n.toLowerCase());
}

/** Whose name starts at `at` - the longest wins, the first of equals - or null. */
function longestNameAt(lower: string, at: number, names: { uid: string; name: string }[]): string | null {
  let best: { uid: string; len: number } | null = null;
  for (const { uid, name } of names) {
    if (lower.startsWith(name, at) && (!best || name.length > best.len)) best = { uid, len: name.length };
  }
  return best ? best.uid : null;
}

export interface Completion {
  kind: 'command' | 'person' | 'channel';
  /** What the popup shows. */
  label: string;
  /** The text to replace the word under the caret with. */
  insert: string;
}

const COMMANDS: { name: string; hint: string }[] = [
  { name: '/w', hint: 'private vox to somebody' },
  { name: '/t', hint: 'a vox group' },
  { name: '/me', hint: 'an action' },
  { name: '/roll', hint: 'dice' },
  { name: '/ic', hint: 'speak as your combatant' },
  { name: '/ooc', hint: 'speak as yourself' },
];

/**
 * Completions for the word the caret is in: commands after a leading `/`,
 * people after `/w ` or an `@`, groups after `/t `. Empty when there is
 * nothing to offer.
 */
export function completions(
  text: string,
  caret: number,
  ctx: { people: Person[]; channels: ChatChannel[]; me: string },
): Completion[] {
  const before = text.slice(0, caret);
  const word = before.match(/(\S*)$/)?.[1] ?? '';
  if (before === word && word.startsWith('/')) return commandCompletions(word);
  const cmd = before.match(/^\/(w|whisper|t|team|group)\s+(\S*)$/i);
  if (cmd) return targetCompletions(cmd[1], cmd[2].toLowerCase(), ctx);
  if (word.startsWith('@')) return mentionCompletions(word.slice(1).toLowerCase(), ctx);
  return [];
}

type CompletionContext = { people: Person[]; channels: ChatChannel[]; me: string };

/** The slash commands the typed word starts. */
function commandCompletions(word: string): Completion[] {
  const q = word.toLowerCase();
  return COMMANDS.filter((c) => c.name.startsWith(q)).map((c) => ({
    kind: 'command',
    label: `${c.name} - ${c.hint}`,
    insert: `${c.name} `,
  }));
}

/** Who a whisper could go to, or which group a /t could name. */
function targetCompletions(cmd: string, q: string, ctx: CompletionContext): Completion[] {
  if (/^(w|whisper)$/i.test(cmd)) {
    return ctx.people
      .filter((p) => p.uid !== ctx.me && lowerNamesOrBlank(p).some((n) => n.startsWith(q)))
      .map((p) => ({
        kind: 'person',
        label: p.actingAsName ? `${p.name} (${p.actingAsName})` : p.name,
        insert: `${p.name} `,
      }));
  }
  const groups: Completion[] = [
    { kind: 'channel', label: 'Players', insert: 'players ' },
    ...ctx.channels.map((c) => ({ kind: 'channel' as const, label: c.name, insert: `${c.name} ` })),
  ];
  return groups.filter((g) => g.insert.toLowerCase().startsWith(q));
}

/**
 * Both names lower-cased, a missing acting-as name kept as "" - which
 * every query of "" matches, exactly as the whisper list always has.
 */
function lowerNamesOrBlank(p: Person): string[] {
  return [p.name.toLowerCase(), (p.actingAsName ?? '').toLowerCase()];
}

/** The people an @ could mean. */
function mentionCompletions(q: string, ctx: CompletionContext): Completion[] {
  return ctx.people
    .filter((p) => p.uid !== ctx.me && p.name.toLowerCase().startsWith(q))
    .map((p) => ({ kind: 'person', label: p.name, insert: `@${p.name} ` }));
}
